// What the patient banner says about allergies (CLAUDE.md, clinical safety rules).
//
// The banner is never blank about allergies. It says exactly one of:
//
//   1. the list of current allergies;
//   2. "No known allergies", when someone has recorded that and no allergy since;
//   3. "Allergies not recorded", when nobody has recorded anything either way.
//
// Blank would be read as "none", and "none" and "nobody asked" are different clinical
// facts. The result type has no empty variant: a list is a non-empty tuple, so code that
// produces an empty list of allergies does not compile.
//
// The status and verification values are FHIR R4 AllergyIntolerance's value sets, not
// thresholds of our own. Which allergies block a prescription is a separate rule, decided
// with the clinical safety lead when prescribing is built (TODO(clinical-verify)); this
// module only decides what the banner shows.

export const ALLERGY_CLINICAL_STATUSES = ['active', 'inactive', 'resolved'] as const;
export type AllergyClinicalStatus = (typeof ALLERGY_CLINICAL_STATUSES)[number];

export const ALLERGY_VERIFICATION_STATUSES = [
  'unconfirmed',
  'confirmed',
  'refuted',
  'entered-in-error',
] as const;
export type AllergyVerificationStatus = (typeof ALLERGY_VERIFICATION_STATUSES)[number];

export const ALLERGY_CATEGORIES = ['food', 'medication', 'environment', 'biologic'] as const;
export type AllergyCategory = (typeof ALLERGY_CATEGORIES)[number];

export const ALLERGY_CRITICALITIES = ['low', 'high', 'unable-to-assess'] as const;
export type AllergyCriticality = (typeof ALLERGY_CRITICALITIES)[number];

export const ALLERGY_REACTION_SEVERITIES = ['mild', 'moderate', 'severe'] as const;
export type AllergyReactionSeverity = (typeof ALLERGY_REACTION_SEVERITIES)[number];

/** One recorded allergy or intolerance, as the banner needs it. */
export interface AllergyRecord {
  readonly id: string;
  readonly substanceDisplay: string;
  /** The reaction as recorded, free text, e.g. what the patient reported. */
  readonly reactionText: string | null;
  readonly category: AllergyCategory | null;
  readonly criticality: AllergyCriticality | null;
  readonly clinicalStatus: AllergyClinicalStatus;
  readonly verificationStatus: AllergyVerificationStatus;
}

/** A recorded statement about allergies as a whole. Only "no known allergies" exists. */
export interface AllergyAssertionRecord {
  readonly assertion: 'no_known_allergies';
  readonly status: 'active' | 'superseded' | 'entered_in_error';
}

export interface AllergySummaryItem {
  readonly id: string;
  readonly substanceDisplay: string;
  readonly reactionText: string | null;
  readonly category: AllergyCategory | null;
  readonly criticality: AllergyCriticality | null;
  readonly verificationStatus: 'unconfirmed' | 'confirmed';
}

export type AllergySummary =
  | {
      readonly kind: 'allergies';
      readonly allergies: readonly [AllergySummaryItem, ...AllergySummaryItem[]];
    }
  | { readonly kind: 'no_known_allergies' }
  | { readonly kind: 'not_recorded' };

/** The words the banner uses for the two states without a list (CLAUDE.md). */
export const ALLERGY_SUMMARY_LABELS = {
  no_known_allergies: 'No known allergies',
  not_recorded: 'Allergies not recorded',
} as const;

/**
 * Current means active and not refuted or entered in error. An unconfirmed allergy is
 * shown: a suspected penicillin allergy is exactly what a prescriber must see.
 */
export function isCurrentAllergy(allergy: AllergyRecord): boolean {
  return (
    allergy.clinicalStatus === 'active' &&
    allergy.verificationStatus !== 'refuted' &&
    allergy.verificationStatus !== 'entered-in-error'
  );
}

/**
 * The banner's allergy state. Allergies keep the order they were given in (the API
 * passes them oldest first), so the banner never reorders between two loads.
 */
export function summariseAllergies(
  allergies: readonly AllergyRecord[],
  assertions: readonly AllergyAssertionRecord[],
): AllergySummary {
  const current: AllergySummaryItem[] = [];
  for (const allergy of allergies) {
    if (!isCurrentAllergy(allergy)) continue;
    current.push({
      id: allergy.id,
      substanceDisplay: allergy.substanceDisplay,
      reactionText: allergy.reactionText,
      category: allergy.category,
      criticality: allergy.criticality,
      // isCurrentAllergy has excluded the other two values.
      verificationStatus: allergy.verificationStatus === 'confirmed' ? 'confirmed' : 'unconfirmed',
    });
  }
  const [first, ...rest] = current;
  if (first !== undefined) return { kind: 'allergies', allergies: [first, ...rest] };

  // "No known allergies" is the only assertion there is; a new kind will need its own branch.
  const noKnownAllergies = assertions.some((a) => a.status === 'active');
  if (noKnownAllergies) return { kind: 'no_known_allergies' };

  return { kind: 'not_recorded' };
}
